import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ERRORS } from '@/constants';
import { ApiError } from '@/services/api/client';
import {
  useCompletePasswordReset,
  useRequestPasswordReset,
  useVerifyPasswordReset,
} from '@/services/queries';
import { splitFormErrors, toFormErrors } from '@/shared/lib/forms';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { BackLink, CodeInput, FormError, PasswordField, TextField } from './fields';
import { LINK, SUBMIT, SUBTITLE, TIMER_LINE, TIMER_VALUE, TITLE } from './styles';

/**
 * Self-service password reset, in three steps on one screen.
 *
 *   email    - where to send the code
 *   code     - the six digits from that email
 *   password - the new password, twice
 *
 * Held in component state rather than routed, for the same reason the MFA step
 * is: the reset token only exists in memory, so a `/reset-password/new` URL
 * would be a page that is broken on reload and bookmarkable into a dead end. A
 * reload starts over, which is correct.
 *
 * The server says "sent" whether or not the address has an account, so this
 * says "if an account exists" rather than claiming a code is on its way.
 */

/**
 * Matches the server's resend cooldown. The server silently ignores an earlier
 * resend, so the button stays disabled until one would actually be honoured.
 */
const RESEND_SECONDS = 60;

/** Mirrors the server's rule - see resetCompleteSchema in user/auth.routes.ts. */
const MIN_PASSWORD = 12;

type Step =
  { name: 'email' } | { name: 'code'; email: string } | { name: 'password'; resetToken: string };

/** Turns an ApiError into banner copy. Field errors are placed by each form. */
function describe(error: ApiError, values: unknown): string | null {
  if (error.fieldErrors) {
    const { unplaced } = splitFormErrors(toFormErrors(error.fieldErrors), values);
    return unplaced.length > 0 ? error.message : null;
  }
  if (error.code === 'reset_code_invalid') return ERRORS.resetCodeInvalid;
  if (error.code === 'reset_expired') return ERRORS.resetExpired;
  if (error.code === 'rate_limited') return ERRORS.rateLimited;
  return error.message;
}

export function ResetPasswordForm({
  initialEmail = '',
  onComplete,
  onBack,
}: {
  initialEmail?: string;
  /** Back to the sign-in form, from any step. */
  onBack: () => void;
  /** Called with the address once the new password is saved. */
  onComplete: (email: string) => void;
}) {
  const [step, setStep] = useState<Step>({ name: 'email' });
  const [email, setEmail] = useState(initialEmail);
  /** Why the flow was sent back to the start, shown on the email step. */
  const [restartReason, setRestartReason] = useState<string | null>(null);

  if (step.name === 'code') {
    return (
      <CodeStep
        email={step.email}
        onVerified={(resetToken) => setStep({ name: 'password', resetToken })}
        onChangeEmail={() => setStep({ name: 'email' })}
        onBack={onBack}
      />
    );
  }

  if (step.name === 'password') {
    return (
      <PasswordStep
        resetToken={step.resetToken}
        onDone={() => onComplete(email)}
        onExpired={() => {
          setRestartReason(ERRORS.resetExpired);
          setStep({ name: 'email' });
        }}
        onBack={onBack}
      />
    );
  }

  return (
    <EmailStep
      initialEmail={email}
      notice={restartReason}
      onSent={(sentTo) => {
        setEmail(sentTo);
        setRestartReason(null);
        setStep({ name: 'code', email: sentTo });
      }}
      onBack={onBack}
    />
  );
}

function EmailStep({
  initialEmail,
  notice,
  onSent,
  onBack,
}: {
  initialEmail: string;
  notice: string | null;
  onSent: (email: string) => void;
  onBack: () => void;
}) {
  const request = useRequestPasswordReset();

  const formik = useFormik({
    initialValues: { email: initialEmail },
    validationSchema: Yup.object({
      email: Yup.string()
        .trim()
        .required('Enter your email address.')
        .email('Enter a valid email address.')
        .max(320, 'That address is too long.'),
    }),
    onSubmit: (values, helpers) => {
      const address = values.email.trim();
      request.mutate(
        { email: address },
        {
          onSuccess: () => onSent(address),
          onError: (error) => {
            if (error instanceof ApiError && error.fieldErrors) {
              helpers.setErrors(splitFormErrors(toFormErrors(error.fieldErrors), values).placed);
            }
          },
        },
      );
    },
  });

  const apiError = request.error instanceof ApiError ? request.error : null;
  const error = formik.touched.email ? formik.errors.email : undefined;

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-4" noValidate>
      <header>
        <h1 className={TITLE}>Reset password</h1>
        <p className={SUBTITLE}>
          Enter the email address your NuDent account is registered to and we'll send you a 6-digit
          verification code.
        </p>
      </header>

      <FormError
        message={apiError ? describe(apiError, formik.values) : notice}
        {...(apiError?.requestId ? { requestId: apiError.requestId } : {})}
      />

      <TextField
        id="reset-email"
        label="Email"
        type="email"
        autoComplete="username"
        autoFocus
        placeholder="you@yourlab.com"
        {...formik.getFieldProps('email')}
        error={error}
      />

      <Button type="submit" className={SUBMIT} disabled={request.isPending}>
        {request.isPending ? 'Sending code…' : 'Send code'}
      </Button>

      <BackLink label="Back to sign in" onClick={onBack} />
    </form>
  );
}

function CodeStep({
  email,
  onVerified,
  onChangeEmail,
  onBack,
}: {
  email: string;
  onVerified: (resetToken: string) => void;
  onChangeEmail: () => void;
  onBack: () => void;
}) {
  const verify = useVerifyPasswordReset();
  const resend = useRequestPasswordReset();
  // The code was just sent to reach this step, so the cooldown starts full.
  const [cooldown, setCooldown] = useState(RESEND_SECONDS);
  const [resent, setResent] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setTimeout(() => setCooldown((left) => left - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  const formik = useFormik({
    initialValues: { code: '' },
    validationSchema: Yup.object({
      code: Yup.string()
        .required('Enter the 6-digit code from the email.')
        .matches(/^\d{6}$/, 'The code is six digits.'),
    }),
    onSubmit: (values) => {
      verify.mutate(
        { email, code: values.code },
        {
          onSuccess: (result) => onVerified(result.resetToken),
          // A wrong code is worth clearing: the next attempt is a fresh
          // six digits, not an edit of the last one. Untouched again too, so
          // the retry is not told "six digits" after its first keystroke.
          onError: () => {
            void formik.setFieldValue('code', '', false);
            void formik.setFieldTouched('code', false, false);
          },
        },
      );
    },
  });

  const apiError = verify.error instanceof ApiError ? verify.error : null;
  const resendError = resend.error instanceof ApiError ? resend.error : null;
  const error = formik.touched.code ? formik.errors.code : undefined;

  const doResend = () => {
    verify.reset();
    resend.mutate(
      { email },
      {
        onSuccess: () => {
          setResent(true);
          setCooldown(RESEND_SECONDS);
        },
      },
    );
  };

  const clock = `${String(Math.floor(cooldown / 60))}:${String(cooldown % 60).padStart(2, '0')}`;

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-4" noValidate>
      <header>
        <h1 className={TITLE}>Enter verification code</h1>
        <p className={SUBTITLE}>
          If an account exists for <span className="font-mono text-[#1B2434]">{email}</span>, we've
          sent it a 6-digit code. It expires in 10 minutes.{' '}
          <button type="button" onClick={onChangeEmail} className={LINK}>
            Use a different email
          </button>
        </p>
      </header>

      <FormError
        message={
          apiError
            ? describe(apiError, formik.values)
            : resendError
              ? describe(resendError, {})
              : null
        }
        {...((apiError ?? resendError)?.requestId
          ? { requestId: (apiError ?? resendError)?.requestId ?? '' }
          : {})}
      />

      <CodeInput
        id="reset-code"
        label="Verification code"
        autoFocus
        disabled={verify.isPending}
        value={formik.values.code}
        onChange={(value) => void formik.setFieldValue('code', value)}
        onBlur={() => void formik.setFieldTouched('code', true)}
        {...(error ? { error } : {})}
      />

      <Button type="submit" className={SUBMIT} disabled={verify.isPending}>
        {verify.isPending ? 'Verifying…' : 'Verify code'}
      </Button>

      <p className={TIMER_LINE} aria-live="polite">
        {cooldown > 0 ? (
          <>
            {resent ? 'New code sent. ' : "Didn't get it? "}Resend in{' '}
            <span className={TIMER_VALUE}>{clock}</span>
          </>
        ) : (
          <>
            Didn't get it?{' '}
            <button
              type="button"
              onClick={doResend}
              disabled={resend.isPending}
              className={cn(LINK, 'disabled:cursor-wait disabled:opacity-60')}
            >
              {resend.isPending ? 'Sending…' : 'Resend code'}
            </button>
          </>
        )}
      </p>

      <BackLink label="Back to sign in" onClick={onBack} />
    </form>
  );
}

function PasswordStep({
  resetToken,
  onDone,
  onExpired,
  onBack,
}: {
  resetToken: string;
  onDone: () => void;
  onExpired: () => void;
  onBack: () => void;
}) {
  const complete = useCompletePasswordReset();

  const formik = useFormik({
    initialValues: { password: '', confirm: '' },
    validationSchema: Yup.object({
      password: Yup.string()
        .required('Enter a new password.')
        .min(MIN_PASSWORD, `Use at least ${String(MIN_PASSWORD)} characters.`)
        .max(200, 'That password is too long.'),
      confirm: Yup.string()
        .required('Confirm your new password.')
        .oneOf([Yup.ref('password')], 'The passwords do not match.'),
    }),
    onSubmit: (values, helpers) => {
      complete.mutate(
        { resetToken, password: values.password },
        {
          onSuccess: onDone,
          onError: (error) => {
            if (!(error instanceof ApiError)) return;
            // The token is single-use and short-lived; once it is gone the
            // only way forward is a fresh code.
            if (error.code === 'reset_expired') {
              onExpired();
              return;
            }
            if (error.fieldErrors) {
              helpers.setErrors(splitFormErrors(toFormErrors(error.fieldErrors), values).placed);
            }
          },
        },
      );
    },
  });

  const apiError =
    complete.error instanceof ApiError && complete.error.code !== 'reset_expired'
      ? complete.error
      : null;
  const error = (field: 'password' | 'confirm'): string | undefined =>
    formik.touched[field] ? formik.errors[field] : undefined;

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-4" noValidate>
      <header>
        <h1 className={TITLE}>Choose a new password</h1>
        <p className={SUBTITLE}>
          Code verified. Choose a new password for your NuDent account - you'll be signed out
          everywhere else once it's saved.
        </p>
      </header>

      <FormError
        message={apiError ? describe(apiError, formik.values) : null}
        {...(apiError?.requestId ? { requestId: apiError.requestId } : {})}
      />

      <PasswordField
        id="new-password"
        label="New password"
        autoComplete="new-password"
        autoFocus
        hint={`At least ${String(MIN_PASSWORD)} characters.`}
        {...formik.getFieldProps('password')}
        error={error('password')}
      />

      <PasswordField
        id="confirm-password"
        label="Confirm new password"
        autoComplete="new-password"
        {...formik.getFieldProps('confirm')}
        error={error('confirm')}
      />

      <Button type="submit" className={SUBMIT} disabled={complete.isPending}>
        {complete.isPending ? 'Saving…' : 'Reset password'}
      </Button>

      <BackLink label="Back to sign in" onClick={onBack} />
    </form>
  );
}
